import { Link } from 'react-router-dom';
import { useDashboardUi } from '@/components/layout/UserLayout';
import {
  IconCreditCard,
  IconPhone,
  IconShield,
  IconTv,
  IconWifi,
  IconZap,
} from '@/components/landing/LandingIcons';
import { LayoutGrid } from 'lucide-react';
import { IconNavGrid, IconNavReceipt } from '@/components/icons/DashboardIcons';
import { ComingSoon } from '@/components/shared/Placeholders';
import { RecentTransactionsCard } from '@/components/transactions/RecentTransactionsCard';
import { QuickActionCard } from '@/components/user/QuickActionCard';
import { WalletOverviewCard } from '@/components/user/WalletOverviewCard';
import { GetTheAppDisclosure } from '@/components/shared/StoreBadges';
import { ROUTES } from '@/constants/routes';
import { useUserAuth } from '@/context/UserAuthContext';
import { useAsyncAction } from '@/hooks/useAsyncAction';

export function UserDashboardHome() {
  const { user, refreshProfile } = useUserAuth();
  const {
    openFundModal,
    wallet,
    walletLoading,
    generatingVa,
    loadWallet,
    generateVirtualAccount,
    fundingEnabled,
  } = useDashboardUi();
  const { loading: refreshing, run } = useAsyncAction();

  async function handleRefreshBalance() {
    await run(async () => {
      await Promise.all([refreshProfile(), loadWallet()]);
    });
  }

  const balance = wallet?.balance ?? user?.walletBalance ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-brand-navy sm:text-2xl">Overview</h1>
          <p className="text-xs text-brand-muted sm:text-sm">Your wallet and quick services</p>
        </div>
        <GetTheAppDisclosure />
      </div>

      <WalletOverviewCard
        balance={balance}
        account={wallet?.virtualAccount ?? null}
        onFundClick={openFundModal}
        onRefresh={() => void handleRefreshBalance()}
        onGenerate={() => void generateVirtualAccount()}
        refreshing={refreshing || walletLoading}
        generating={generatingVa}
        fundingEnabled={fundingEnabled}
      />

      <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-soft sm:p-4 lg:p-5">
        <h2 className="text-sm font-bold text-brand-navy">Quick actions</h2>
        <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3">
          <QuickActionCard
            title="Airtime"
            to={ROUTES.dashboardAirtime}
            tone="blue"
            variant="compact"
            icon={<IconPhone className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Data"
            to={ROUTES.dashboardData}
            tone="purple"
            variant="compact"
            icon={<IconWifi className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Electricity"
            to={ROUTES.dashboardElectricity}
            tone="orange"
            variant="compact"
            icon={<IconZap className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Cable TV"
            to={ROUTES.dashboardCableTv}
            tone="green"
            variant="compact"
            icon={<IconTv className="h-5 w-5" />}
          />
          <QuickActionCard
            title="NIN"
            to={ROUTES.dashboardVerifyNin}
            tone="green"
            variant="compact"
            icon={<IconShield className="h-5 w-5" />}
          />
          <QuickActionCard
            title="BVN"
            to={ROUTES.dashboardVerifyBvn}
            tone="blue"
            variant="compact"
            icon={<IconCreditCard className="h-5 w-5" />}
          />
          <QuickActionCard
            title="Transactions"
            to={ROUTES.dashboardTransactions}
            tone="navy"
            variant="compact"
            icon={<IconNavReceipt className="h-5 w-5" />}
          />
          <div className="contents lg:hidden">
            <QuickActionCard
              title="More"
              to={ROUTES.dashboardMore}
              tone="orange"
              variant="compact"
              icon={<LayoutGrid className="h-5 w-5" />}
            />
          </div>
          <div className="hidden lg:contents">
            <QuickActionCard
              title="Verification"
              to={ROUTES.dashboardServices}
              tone="orange"
              variant="compact"
              icon={<IconNavGrid className="h-5 w-5" />}
            />
          </div>
        </div>
      </div>

      <RecentTransactionsCard />

      <div aria-hidden className="h-8 sm:h-12" />
    </div>
  );
}

export function UserServicesPage() {
  const services = [
    {
      id: 'nin',
      title: 'NIN Verification',
      subtitle: 'By NIN, phone, tracking ID, or demography',
      features: ['Regular, Standard, Premium & Advanced slips', 'Instant PDF download'],
      to: ROUTES.dashboardVerifyNin,
      icon: '/icons/nin.svg',
    },
    {
      id: 'bvn',
      title: 'BVN Verification',
      subtitle: 'By BVN number or linked phone number',
      features: ['Basic & Advanced verification', 'Lookup BVN from phone (081…)'],
      to: ROUTES.dashboardVerifyBvn,
      icon: '/icons/bvn.svg',
    },
  ] as const;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">Verification Services</h1>
        <p className="text-sm text-brand-muted">
          Verify NIN or BVN — pay from your wallet and download slips instantly.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {services.map((service) => (
          <Link
            key={service.id}
            to={service.to}
            className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-brand-green/30 hover:shadow-lg"
          >
            <div className="bg-gradient-to-r from-brand-green to-emerald-600 p-5 text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/20 p-2">
                    <img src={service.icon} alt="" className="h-7 w-7" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-bold md:text-xl">{service.title}</h2>
                    <p className="mt-0.5 text-sm text-white/90">{service.subtitle}</p>
                  </div>
                </div>
                <span className="text-white/80 transition group-hover:translate-x-0.5">→</span>
              </div>
            </div>
            <div className="p-5">
              <ul className="space-y-2">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-green" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm font-semibold text-brand-green group-hover:underline">
                Start verification →
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const pageDescriptions: Record<string, string> = {
  Airtime: 'Purchase airtime via Quicklysim and VTpass.',
  'Data Bundles': 'Buy cheap data bundles via VTpass.',
  Electricity: 'Pay electricity bills for all major DisCos via VTpass.',
  'Cable TV': 'Subscribe to DSTV, GOTV, and Startimes via VTpass.',
  Exams: 'Buy WAEC, NECO, and NABTEB pins via VTpass.',
  'NIN Verification': 'Verify NIN and download official-style slips.',
  'BVN Verification': 'Verify BVN by number or linked phone.',
  Transactions: 'View your transaction history and receipts.',
};

export function UserPlaceholderPage({ title }: { title: string }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">{title}</h1>
        <p className="text-sm text-brand-muted">{pageDescriptions[title] ?? 'Coming soon.'}</p>
      </div>
      <ComingSoon title={title} description={pageDescriptions[title]} />
    </div>
  );
}
